/**
 * Environment NPCs of 绿色码头. Names, roles and origins characterise the place only — they add
 * no lore beyond story.txt (short brown-skinned 亚北 traders doing the best business, sailors of
 * several origins, tall pale people, porters, girls and elders who shelter when it rains while
 * the young carry on).
 *
 * IDS: an NPC id is its actor key, `gh-<short>` — the runtime key convention of tools/assets/README.md
 * (`ACTOR_DEFS[<id>].texture === 'actor-<id>'`), so the cast sheets the pipeline exports under
 * `actor-gh-<short>` (public/assets/manifest.json, group `green-harbor`) are picked up by key alone.
 * The characters builder ships the canonical `ACTOR_DEFS['gh-*']` entries; `look` below is the same
 * def as a fallback that `register.ts` installs only when an id is missing (never overriding).
 * `template` names the shared cast template (`actor-npc-*`, manifest group `shared`) the variant
 * was recoloured from — the fallback texture before the procedural placeholder.
 *
 * UNITS: every coordinate handed to the engine is WORLD PIXELS (feet position, bottom-centre of
 * the tile). Authoring below is in tiles; `feet()` converts. `IActor.walkTo`, the characters
 * `ScheduleProgram` and the world `NpcScheduleRunner` all take pixels (contracts.ts).
 */
import type { MapNpcPlacement, NpcScheduleStep } from '@/core/types/contracts';
import type { Facing } from '@/core/types';
import type { ActorDef } from '@/characters/actorDefs';
import { ACTOR, ghNpcId } from './actors';
import { feet, span, type TileRect } from '../maps/coords';

export type NpcOrigin = 'yabei' | 'zhongyuan' | 'local' | 'pale' | 'foreign';

/** A vendor behind a stall: the row in front of the counter is one prompt — look at the goods, the vendor answers. */
export interface StallFront {
  /** Tiles in front of the counter (world rect of the `talk` interaction). */
  front: TileRect;
  /** Prompt label (中文). */
  label: string;
  /** Merged dialogue id (`gh.stall.<short>`): observation lines first, then the vendor's lines. */
  dialogue: string;
  /** Observation dialogue the goods lines come from (`gh.obs.*`). */
  observe: string;
}

export interface HarborNpcDef {
  /** Actor key and placement id (`gh-<short>`). */
  id: string;
  /** Data-local name: dialogue `gh.npc.<short>`, mark `talked:<short>`. */
  short: string;
  /** Display name (Chinese, live text). */
  name: string;
  role: string;
  origin: NpcOrigin;
  /** Fallback actor definition (installed under `id` only when the characters builder has none). */
  look: ActorDef;
  /** Cast sheet key the pipeline exports for this NPC (`actor-gh-<short>`). */
  sheet: string;
  /** Shared cast template the sheet is a variant of (`actor-npc-*`), used as a texture alias. */
  template: string;
  /** Young ones keep working in the rain (story: 年轻一点的就满不在乎的继续做自己的事). */
  young: boolean;
  placement: MapNpcPlacement;
  stall?: StallFront;
}

const walk = (tx: number, ty: number): NpcScheduleStep => ({ kind: 'walk', ...feet(tx, ty) });
const wait = (ms: number): NpcScheduleStep => ({ kind: 'wait', ms });
const face = (facing: Facing): NpcScheduleStep => ({ kind: 'face', facing });
const emote = (e: string, ms = 900): NpcScheduleStep => ({ kind: 'emote', emote: e, ms });
const loop = (): NpcScheduleStep => ({ kind: 'loop' });
/** Shelter behaviour: go somewhere dry, face a direction, and stay. */
const shelter = (tx: number, ty: number, facing: Facing): NpcScheduleStep[] => [walk(tx, ty), face(facing), wait(4000), loop()];

// Dev palettes per origin (ART_DIRECTION: 亚北 traders brown-skinned in bright dyed cotton; tall pale travellers).
const SKIN = { yabei: 0x9b6a46, zhongyuan: 0xd9a878, local: 0xb07a50, pale: 0xf0d6bd, foreign: 0xf0d6bd } as const;
const HAIR = { yabei: 0x2a1c14, zhongyuan: 0x2a1c14, local: 0x1a1210, pale: 0x8a6a3a, foreign: 0xc9b06a } as const;

/** Shared cast templates (docs/progress/art-cast.md C16–C21) the harbor variants were recoloured from. */
export const NPC_TEMPLATES = {
  yabeiTrader: 'actor-npc-yabei-trader',
  paleTrader: 'actor-npc-pale-trader',
  sailor: 'actor-npc-sailor',
  porter: 'actor-npc-porter',
  womanTraveller: 'actor-npc-woman-traveller',
  elderFan: 'actor-npc-elder-fan',
  standingDrinker: 'actor-npc-drinker-a',
} as const;

interface NpcInput {
  short: string; name: string; role: string; origin: NpcOrigin; young: boolean; template: string;
  /** Dev robe / accent colours and a 1–3 char Latin label (never Chinese in images). */
  robe: number; accent: number; label: string; speed?: number;
  x: number; y: number; facing: Facing; dialogue?: string;
  schedule?: NpcScheduleStep[]; drizzle?: NpcScheduleStep[]; rain?: NpcScheduleStep[];
  /** Vendor: tiles in front of the counter + the goods observation the stall prompt starts with. */
  stall?: { front: TileRect; label: string; observe: string };
}

function npc(def: NpcInput): HarborNpcDef {
  const { x, y, facing, dialogue, schedule, drizzle, rain, robe, accent, label, speed, stall, ...rest } = def;
  const id = ghNpcId(rest.short);
  const weatherBehaviour: MapNpcPlacement['weatherBehaviour'] = {};
  if (drizzle) weatherBehaviour.drizzle = drizzle;
  if (rain) weatherBehaviour.rain = rain;
  if (rain && !drizzle) weatherBehaviour.drizzle = rain;
  if (drizzle && !rain) weatherBehaviour.rain = drizzle;
  const sheet = `actor-${id}`;
  const look: ActorDef = {
    texture: sheet,
    textureAliases: [rest.template],
    displayName: rest.name,
    portrait: `portrait-${id}`,
    speed: speed ?? 70,
    dev: { robe, accent, skin: SKIN[rest.origin], hair: HAIR[rest.origin], label },
  };
  const out: HarborNpcDef = {
    ...rest,
    id,
    look,
    sheet,
    placement: {
      id, actor: id, ...feet(x, y), facing,
      dialogue: dialogue ?? `gh.npc.${rest.short}`,
      schedule,
      weatherBehaviour: Object.keys(weatherBehaviour).length ? weatherBehaviour : undefined,
    },
  };
  if (stall) out.stall = { ...stall, dialogue: `gh.stall.${rest.short}` };
  return out;
}

/** The 亚北青年 of the main line keeps the characters builder's `yabei-merchant` key (actor + portrait). */
function merchant(): HarborNpcDef {
  const base = npc({ short: 'yabei-merchant', name: '亚北商贩', role: '亚北商贩（仙人球、袋鼠皮、鸵鸟蛋）', origin: 'yabei', young: true, template: NPC_TEMPLATES.yabeiTrader,
    robe: 0xffb347, accent: 0x1f8f8a, label: 'YB', speed: 90,
    x: 37, y: 22, facing: 'down',
    schedule: [wait(2600), emote('wave'), wait(1800), face('left'), wait(900), face('down'), wait(2200), emote('note'), loop()],
    rain: [face('down'), emote('happy'), wait(3000), loop()] });
  const id = ACTOR.MERCHANT;
  return {
    ...base, id, sheet: `actor-${id}`,
    look: { ...base.look, texture: `actor-${id}`, portrait: `portrait-${id}` },
    placement: { ...base.placement, id, actor: id, dialogue: `gh.npc.${id}` },
  };
}

export const GREEN_HARBOR_NPCS: HarborNpcDef[] = [
  // ---- the 亚北青年 (main line). His stall sits under a palm; young, never leaves his goods.
  merchant(),

  // ---- market vendors (stall fronts: look at the goods, the vendor answers — see maps/green-harbor.ts)
  npc({ short: 'candle-seller', name: '巴图', role: '亚北蜡烛贩 · 一金币一截', origin: 'yabei', young: true, template: NPC_TEMPLATES.yabeiTrader, robe: 0xe2c48c, accent: 0x8c2a22, label: 'BT',
    x: 18, y: 17, facing: 'down',
    schedule: [wait(1500), emote('coin'), wait(2400), face('right'), wait(700), face('down'), loop()],
    rain: [face('down'), emote('coin'), wait(3000), loop()],
    stall: { front: span(17, 20, 19, 20), label: '看看蜡烛', observe: 'gh.obs.candles' } }),
  npc({ short: 'spice-seller', name: '娜依', role: '香料摊主（亚北）', origin: 'yabei', young: false, template: NPC_TEMPLATES.yabeiTrader, robe: 0x8c2a22, accent: 0xe2c48c, label: 'NY',
    x: 23, y: 17, facing: 'down',
    schedule: [wait(3000), face('left'), wait(1200), face('down'), emote('sniff'), loop()],
    drizzle: [face('down'), wait(2000), emote('sigh'), loop()],
    stall: { front: span(22, 20, 24, 20), label: '看看香料', observe: 'gh.obs.spices' } }),
  npc({ short: 'cloth-seller', name: '陈四娘', role: '布匹摊主（中原）', origin: 'zhongyuan', young: false, template: NPC_TEMPLATES.womanTraveller, robe: 0x2b3a5c, accent: 0xc9b89a, label: 'CS',
    x: 28, y: 17, facing: 'down',
    schedule: [wait(2000), face('right'), wait(1500), face('down'), wait(2600), emote('fold'), loop()],
    drizzle: [face('down'), emote('hurry'), wait(2500), loop()],
    stall: { front: span(27, 20, 29, 20), label: '看看布匹', observe: 'gh.obs.cloth' } }),
  npc({ short: 'fruit-seller', name: '阿黎', role: '奇果摊主（南陆本地）', origin: 'local', young: true, template: NPC_TEMPLATES.yabeiTrader, robe: 0x3c7a3a, accent: 0xffb347, label: 'AL',
    x: 33, y: 17, facing: 'down',
    schedule: [wait(1800), emote('laugh'), wait(2400), face('left'), wait(600), face('down'), loop()],
    stall: { front: span(32, 20, 34, 20), label: '看看奇怪的果子', observe: 'gh.obs.fruit' } }),
  npc({ short: 'fish-seller', name: '老梅', role: '鱼贩（南陆本地）', origin: 'local', young: false, template: NPC_TEMPLATES.sailor, robe: 0x6fd3c8, accent: 0x2b3a5c, label: 'LM',
    x: 12, y: 23, facing: 'down',
    schedule: [wait(2200), face('right'), wait(900), face('down'), emote('fan'), loop()],
    drizzle: [face('down'), emote('laugh'), wait(2000), loop()],
    stall: { front: span(11, 26, 13, 26), label: '看看鱼', observe: 'gh.obs.fish' } }),
  npc({ short: 'jar-seller', name: '库尔', role: '大陶罐摊的亚北青年', origin: 'yabei', young: true, template: NPC_TEMPLATES.yabeiTrader, robe: 0x1f8f8a, accent: 0xffb347, label: 'KR',
    x: 27, y: 22, facing: 'down',
    schedule: [wait(1200), emote('shout'), wait(2000), face('right'), wait(800), face('down'), wait(1800), loop()],
    rain: [face('down'), emote('shout'), wait(2600), loop()],
    stall: { front: span(26, 25, 29, 25), label: '看看大陶罐', observe: 'gh.obs.big-jars' } }),

  // ---- piers, ships and cargo
  npc({ short: 'sailor-zhongyuan', name: '王二', role: '中原水手', origin: 'zhongyuan', young: false, template: NPC_TEMPLATES.sailor, robe: 0x1f8f8a, accent: 0xe2c48c, label: 'WE', speed: 78,
    x: 10, y: 9, facing: 'down',
    schedule: [walk(14, 9), face('up'), wait(1600), walk(10, 9), face('down'), wait(1200), loop()],
    drizzle: [emote('shrug'), wait(1500), walk(12, 9), face('up'), wait(3000), loop()],
    rain: shelter(4, 13, 'up') }),
  npc({ short: 'sailor-foreign', name: '哈根', role: '异邦水手（高大、皮肤白皙）', origin: 'foreign', young: true, template: NPC_TEMPLATES.paleTrader, robe: 0x3a3f57, accent: 0xc9b89a, label: 'HG',
    x: 6, y: 7, facing: 'down',
    schedule: [wait(2400), face('left'), wait(1400), face('down'), emote('rope'), wait(1800), loop()],
    rain: [face('up'), emote('laugh'), wait(2000), loop()] }),
  npc({ short: 'porter-1', name: '阿大', role: '脚夫', origin: 'local', young: true, template: NPC_TEMPLATES.porter, robe: 0x8a5a32, accent: 0xe2c48c, label: 'P1', speed: 76,
    x: 17, y: 10, facing: 'down',
    schedule: [walk(24, 10), walk(24, 13), face('down'), wait(900), emote('heave'), walk(24, 10), walk(17, 10), face('up'), wait(700), loop()] }),
  npc({ short: 'porter-2', name: '阿二', role: '脚夫', origin: 'local', young: true, template: NPC_TEMPLATES.porter, robe: 0x6e4524, accent: 0x8ec95a, label: 'P2', speed: 76,
    x: 7, y: 10, facing: 'right',
    schedule: [walk(12, 10), walk(12, 14), face('right'), wait(800), emote('heave'), walk(12, 10), walk(7, 10), face('up'), wait(900), loop()] }),
  npc({ short: 'crane-worker', name: '铁柱', role: '起重架工', origin: 'zhongyuan', young: true, template: NPC_TEMPLATES.sailor, robe: 0x6b6b73, accent: 0x8c2a22, label: 'TZ',
    x: 11, y: 13, facing: 'up',
    schedule: [wait(1600), emote('pull'), wait(2000), face('right'), wait(700), face('up'), loop()] }),
  npc({ short: 'shipwright', name: '老骨', role: '修船匠（南陆本地）', origin: 'local', young: false, template: NPC_TEMPLATES.sailor, robe: 0x9a8a6a, accent: 0x5a3a1e, label: 'LG', speed: 60,
    x: 45, y: 9, facing: 'up',
    schedule: [wait(2000), emote('hammer'), wait(1400), walk(47, 9), face('up'), emote('hammer'), wait(1400), walk(45, 9), loop()],
    drizzle: [walk(46, 9), face('up'), emote('hammer'), wait(3000), loop()],
    rain: shelter(44, 9, 'up') }),

  // ---- travellers, the tall pale trader, girls, an elder
  // 周明's sheet (chr-gh-traveller-map: idle / talk / laugh) has no walk cycle, so he waits where he stands.
  npc({ short: 'traveller', name: '周明', role: '候船的中原旅人', origin: 'zhongyuan', young: false, template: NPC_TEMPLATES.standingDrinker, robe: 0xc9b89a, accent: 0x3a3f57, label: 'ZM',
    x: 20, y: 13, facing: 'up',
    schedule: [wait(3000), face('left'), wait(1200), face('up'), emote('think'), loop()],
    drizzle: [face('down'), emote('sweat'), wait(2600), face('up'), wait(3000), loop()] }),
  npc({ short: 'pale-trader', name: '艾德文', role: '高大白皙的异邦商人', origin: 'pale', young: false, template: NPC_TEMPLATES.paleTrader, robe: 0x2f5a3a, accent: 0xe2c48c, label: 'ED',
    x: 31, y: 22, facing: 'left',
    schedule: [wait(2400), walk(30, 21), face('up'), wait(2000), walk(31, 22), face('left'), wait(1600), loop()],
    drizzle: shelter(25, 22, 'down') }),
  npc({ short: 'girl-1', name: '小葵', role: '本地女孩', origin: 'local', young: false, template: NPC_TEMPLATES.womanTraveller, robe: 0xffb347, accent: 0x8c2a22, label: 'XK',
    x: 43, y: 27, facing: 'right',
    schedule: [wait(2000), emote('giggle'), wait(2600), face('down'), wait(800), face('right'), loop()],
    drizzle: shelter(50, 34, 'up') }),
  npc({ short: 'girl-2', name: '阿蓝', role: '本地女孩', origin: 'local', young: false, template: NPC_TEMPLATES.womanTraveller, robe: 0x6fd3c8, accent: 0xffb347, label: 'AL2',
    x: 44, y: 27, facing: 'left',
    schedule: [wait(2600), face('down'), wait(700), face('left'), emote('giggle'), wait(2000), loop()],
    drizzle: shelter(53, 34, 'up') }),
  // Idles beside the doorway (51,34): no observation rect on or next to that tile, so he is talkable from every side
  // (the small turtles' rects sit at 48–49 / 55–56).
  npc({ short: 'elder', name: '老伯', role: '门口乘凉的老者', origin: 'local', young: false, template: NPC_TEMPLATES.elderFan, robe: 0x7a7a5a, accent: 0xc9b89a, label: 'LB', speed: 50,
    x: 51, y: 34, facing: 'down',
    schedule: [wait(4000), emote('fan'), wait(2600), face('left'), wait(900), face('down'), loop()],
    drizzle: shelter(57, 34, 'up') }),
];

export const GREEN_HARBOR_NPC_BY_ID: Record<string, HarborNpcDef> = Object.fromEntries(GREEN_HARBOR_NPCS.map((n) => [n.id, n]));
/** NPC that owns a dialogue id (`gh.npc.<short>` and the stall-front `gh.stall.<short>`), for facing the speaker. */
export const GREEN_HARBOR_NPC_BY_DIALOGUE: Record<string, HarborNpcDef> = Object.fromEntries(
  GREEN_HARBOR_NPCS.flatMap((n) => [[n.placement.dialogue as string, n], ...(n.stall ? [[n.stall.dialogue, n] as const] : [])]),
);
